import React, { useMemo, useState } from "react";
import { ReviewStatusDot, type ReviewStatus } from "./ReviewStatusBadge";

interface CardSummary {
  id: string;
  name?: string;
  encounter_set?: string;
  front?: { type?: string };
}

interface EncounterSet {
  id: string;
  name: string;
  order?: number;
}

export function CardTree({
  cards,
  encounterSets = [],
  selectedCardId,
  onSelect,
  statusByCardId,
}: {
  cards: CardSummary[];
  encounterSets?: EncounterSet[];
  selectedCardId: string | null;
  onSelect: (cardId: string) => void;
  /** Statut de relecture par id de carte (langue courante), pour afficher un
   * petit repère visuel dans la liste sans naviguer carte par carte. */
  statusByCardId?: Record<string, ReviewStatus>;
}) {
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"tree" | "list">("tree");

  const encounterSetNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const set of encounterSets) map.set(set.id, set.name);
    return map;
  }, [encounterSets]);

  const filtered = useMemo(
    () => cards.filter((c) => (c.name ?? c.id).toLowerCase().includes(search.toLowerCase())),
    [cards, search]
  );

  // Regroupe par encounter set (vrai regroupement de campagne), avec repli
  // sur le type de carte si l'encounter_set est absent ou inconnu.
  const grouped = useMemo(() => {
    const groups = new Map<string, { label: string; order: number; cards: CardSummary[] }>();
    for (const card of filtered) {
      const setId = card.encounter_set;
      const known = setId ? encounterSetNameById.get(setId) : undefined;
      const key = setId ?? `type:${card.front?.type ?? "autre"}`;
      const label = known ?? card.front?.type ?? "Autre";
      if (!groups.has(key)) {
        const order = encounterSets.find((s) => s.id === setId)?.order ?? 999;
        groups.set(key, { label, order, cards: [] });
      }
      groups.get(key)!.cards.push(card);
    }
    return [...groups.values()].sort((a, b) => a.order - b.order);
  }, [filtered, encounterSetNameById, encounterSets]);

  return (
    <nav className="card-tree">
      <input
        className="input"
        placeholder="Rechercher une carte…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="card-tree__view-toggle">
        <button className={view === "tree" ? "active" : ""} onClick={() => setView("tree")}>
          Arborescence
        </button>
        <button className={view === "list" ? "active" : ""} onClick={() => setView("list")}>
          Liste
        </button>
      </div>

      {view === "tree" &&
        grouped.map((group) => (
          <details key={group.label} open>
            <summary>
              {group.label} ({group.cards.length})
            </summary>
            <ul>
              {group.cards.map((c) => (
                <li key={c.id}>
                  <button className={c.id === selectedCardId ? "active" : ""} onClick={() => onSelect(c.id)}>
                    {statusByCardId?.[c.id] && <ReviewStatusDot status={statusByCardId[c.id]} />} {c.name ?? c.id}
                  </button>
                </li>
              ))}
            </ul>
          </details>
        ))}

      {view === "list" && (
        <ul>
          {filtered.map((c) => (
            <li key={c.id}>
              <button className={c.id === selectedCardId ? "active" : ""} onClick={() => onSelect(c.id)}>
                {statusByCardId?.[c.id] && <ReviewStatusDot status={statusByCardId[c.id]} />} {c.name ?? c.id}
              </button>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
